<template>
  <div class="gen-settings">
    <div class="gen-grid">
    <!-- ───────────── Left column ───────────── -->
    <div class="gen-col">
      <!-- Details -->
      <section class="set-card">
        <header class="set-head">
          <h3 class="set-title">Details</h3>
          <p class="set-sub">Shown on the asset card and in reports.</p>
        </header>
        <div class="set-body">
          <label class="fld-label" for="asset-name">Name</label>
          <v-text-field id="asset-name" v-model="assetName" variant="outlined" density="comfortable"
            hide-details="auto" placeholder="Asset name" class="fld" />

          <label class="fld-label" for="asset-site">Site</label>
          <v-select id="asset-site" v-model="selectedSiteId" :items="siteItems" item-title="title" item-value="id"
            variant="outlined" density="comfortable" hide-details="auto" class="fld" />

          <label class="fld-label" for="asset-desc">Description <span class="fld-opt">· optional</span></label>
          <v-textarea id="asset-desc" v-model="assetDescription" variant="outlined" density="comfortable"
            hide-details="auto" rows="3" auto-grow placeholder="What is it, where is it, anything the team should know"
            class="fld" />
        </div>
      </section>

      <!-- Notifications (hidden on mobile) -->
      <section v-if="!isMobile" class="set-card">
        <header class="set-head">
          <h3 class="set-title">Notifications</h3>
          <p class="set-sub">Post new findings for this asset to a Microsoft Teams channel.</p>
        </header>
        <div class="set-body">
          <label class="fld-label" for="asset-webhook">Teams webhook URL</label>
          <div class="webhook-row">
            <v-text-field id="asset-webhook" v-model="assetTeamsWebHook" variant="outlined" density="comfortable"
              hide-details="auto" placeholder="https://…webhook.office.com/…" class="fld webhook-input" />
            <button type="button" class="btn-outline" :disabled="!canSendTest || sendingTest" @click="sendTeamsTest">
              <v-progress-circular v-if="sendingTest" indeterminate size="16" width="2" />
              <span v-else>Send test</span>
            </button>
          </div>
          <p v-if="testResult" class="webhook-result" :class="{ ok: testResult.ok, bad: !testResult.ok }">
            {{ testResult.msg }}
          </p>
        </div>
      </section>
    </div>

    <!-- ───────────── Right column ───────────── -->
    <div class="gen-col">
      <!-- Cover image -->
      <section class="set-card">
        <header class="set-head">
          <h3 class="set-title">Cover image</h3>
          <p class="set-sub">Used on the asset list and card. 16:9 works best.</p>
        </header>
        <div class="set-body">
          <div class="cover-drop" :class="{ dragover: isDragover, busy: isUploading }"
            @dragover.prevent="isDragover = true" @dragleave.prevent="isDragover = false" @drop.prevent="onDrop">
            <img v-if="coverImage" :src="coverImage" :key="imageKey" alt="Asset cover" class="cover-img"
              @error="onCoverError" />

            <div class="cover-overlay" :class="{ 'has-image': !!coverImage }">
              <template v-if="isUploading">
                <v-progress-circular indeterminate size="28" width="3" color="#64ffda" />
                <span class="cover-text">Uploading…</span>
              </template>
              <template v-else>
                <v-icon v-if="!coverImage" size="30" color="#7f939d">mdi-image-outline</v-icon>
                <span v-if="!coverImage" class="cover-text">Drop an image here</span>
                <div class="cover-actions">
                  <button type="button" class="btn-outline" @click="pickFile">Upload image</button>
                  <v-tooltip :disabled="hasFrames" location="top" text="No walkdown frames available yet.">
                    <template #activator="{ props: tip }">
                      <span v-bind="tip">
                        <button type="button" class="btn-outline accent" :disabled="!hasFrames" @click="openFrames">
                          Use a walkdown frame
                        </button>
                      </span>
                    </template>
                  </v-tooltip>
                </div>
              </template>
            </div>
          </div>
          <input ref="fileInput" type="file" accept="image/*" hidden @change="onFilePicked" />
        </div>
      </section>

      <!-- Delete (archive) asset -->
      <section v-if="canDelete" class="set-card danger-card">
        <div class="danger-row">
          <div class="danger-text">
            <h3 class="set-title">Delete asset</h3>
            <p class="set-sub">Archives the asset and removes it from your active list. You can reactivate it later from the admin area.</p>
          </div>
          <button type="button" class="btn-danger" @click="deleteDialog = true">Delete…</button>
        </div>
      </section>
    </div>

    <!-- Unsaved-changes bar (spans both columns) -->
    <transition name="savebar">
      <div v-if="isDirty" class="save-bar">
        <span class="save-msg"><v-icon size="16" color="#f0b54a">mdi-circle-medium</v-icon>You have unsaved changes</span>
        <div class="save-actions">
          <button type="button" class="btn-ghost" :disabled="isSaving" @click="discardChanges">Discard</button>
          <button type="button" class="btn-primary" :disabled="isSaving" @click="saveDetails">
            <v-progress-circular v-if="isSaving" indeterminate size="16" width="2" />
            <span v-else>Save changes</span>
          </button>
        </div>
      </div>
    </transition>
    </div>

    <!-- Walkdown frame picker -->
    <v-dialog v-model="framesDialog" max-width="860">
      <div class="modal">
        <header class="modal-head">
          <h2 class="modal-title">Choose a walkdown frame</h2>
          <button type="button" class="modal-close" aria-label="Close" @click="framesDialog = false">
            <v-icon size="20">mdi-close</v-icon>
          </button>
        </header>
        <div v-if="framesLoading" class="frames-loading">
          <v-progress-circular indeterminate size="30" width="3" color="#64ffda" />
        </div>
        <div v-else-if="!frameThumbs.length" class="frames-empty">No frames available for this asset yet.</div>
        <div v-else class="frames-grid">
          <button v-for="f in frameThumbs" :key="f.id" type="button" class="frame-cell" @click="useFrame(f)">
            <img v-if="f.url" :src="f.url" :alt="`Frame ${f.id}`" loading="lazy" />
            <span v-else class="frame-ph"><v-icon size="22" color="#4a5a60">mdi-image-outline</v-icon></span>
          </button>
        </div>
      </div>
    </v-dialog>

    <!-- Archive confirmation -->
    <v-dialog v-model="deleteDialog" max-width="440" persistent>
      <div class="modal">
        <div class="danger-icon"><v-icon size="26" color="#f87171">mdi-archive-outline</v-icon></div>
        <h2 class="modal-title center">Delete “{{ storedAsset && storedAsset.name }}”?</h2>
        <p class="modal-text">
          This archives the asset and hides it from your active list. You can reactivate it any time from the admin area.
        </p>
        <div class="modal-actions">
          <button type="button" class="btn-ghost" :disabled="isDeleting" @click="deleteDialog = false">Cancel</button>
          <button type="button" class="btn-danger" :disabled="isDeleting" @click="confirmDelete">
            <v-progress-circular v-if="isDeleting" indeterminate size="16" width="2" />
            <span v-else>Delete asset</span>
          </button>
        </div>
      </div>
    </v-dialog>
  </div>
</template>


<script setup>
import { defineOptions, computed, onMounted, onUnmounted, ref } from 'vue';
import { useDisplay } from 'vuetify';
import { useRouter } from 'vue-router';
import { useAssetStore } from '@/store/assetStore';
import { getBase64AndExtension } from '@/utils/FileManipulation';
import UpdateAssetModel from '@/domain/asset/UpdateAssetModel';
import UpdateAssetImageModel from '@/domain/asset/UpdateAssetImageModel';
import AssetService from '@/services/AssetService';
import SiteService from '@/services/SiteService';
import ModelVersionService from '@/services/ModelVersionService';
import organizationSasService from '@/services/OrganizationSasService';
import { Permissions } from '@/constants/permissions';
import { usePermissions } from '@/composable/usePermissions';

defineOptions({ name: 'UpdateAssetAdminCard' });

const router = useRouter();
const assetStore = useAssetStore();
const { can } = usePermissions();
const { smAndDown: isMobile } = useDisplay();

// Archive is limited to asset admins, tenant (org) admins and Duallo admins.
const canDelete = computed(() =>
  can(Permissions.AssetAdminView) || can(Permissions.OrganizationAdminView) || can(Permissions.DualloAdminView));

// ── Details / notifications ──
const storedAsset = ref(null);
const assetName = ref('');
const assetDescription = ref('');
const assetTeamsWebHook = ref('');
const selectedSiteId = ref(null);
const sites = ref([]);
const isSaving = ref(false);

const siteItems = computed(() => [{ id: null, title: 'No site' }, ...sites.value]);

const isDirty = computed(() => {
  const a = storedAsset.value;
  if (!a) return false;
  return (assetName.value || '') !== (a.name || '')
    || (assetDescription.value || '') !== (a.description || '')
    || (assetTeamsWebHook.value || '') !== (a.teamsWebhookUrl || '')
    || (selectedSiteId.value ?? null) !== (a.siteId ?? null);
});

// ── Teams test ──
const sendingTest = ref(false);
const testResult = ref(null);
const canSendTest = computed(() => !!(assetTeamsWebHook.value && assetTeamsWebHook.value.trim()));

// ── Cover image ──
const fileInput = ref(null);
const imageSrc = ref(null);
const coverPreview = ref(null);
const coverImage = computed(() => coverPreview.value || imageSrc.value);
const imageKey = ref(0);
const isUploading = ref(false);
const isDragover = ref(false);

// ── Walkdown frames ──
const hasFrames = ref(false);
const framesDialog = ref(false);
const framesLoading = ref(false);
const frameThumbs = ref([]);
let framesRaw = [];

// ── Archive ──
const deleteDialog = ref(false);
const isDeleting = ref(false);

onMounted(async () => {
  storedAsset.value = assetStore.asset;
  if (!storedAsset.value) return;
  resetFieldsFromStore();
  imageSrc.value = await organizationSasService.getSignedUrl(storedAsset.value.imagePath);
  loadSites();
  probeFrames();
});

function resetFieldsFromStore() {
  const a = storedAsset.value;
  assetName.value = a.name || '';
  assetDescription.value = a.description || '';
  assetTeamsWebHook.value = a.teamsWebhookUrl || '';
  selectedSiteId.value = a.siteId ?? null;
}

async function loadSites() {
  try {
    sites.value = await SiteService.getSitesByOrganizationId(storedAsset.value.organizationId);
  } catch {
    sites.value = [];
  }
}

// Best-effort: does the current model version have frames we could use as a cover?
async function probeFrames() {
  try {
    framesRaw = await ModelVersionService.getFramesByCurrentModelVersion(storedAsset.value.id);
    hasFrames.value = Array.isArray(framesRaw) && framesRaw.length > 0;
  } catch {
    hasFrames.value = false;
  }
}

// ── Save / discard ──
async function saveDetails() {
  if (!isDirty.value) return;
  isSaving.value = true;
  const record = new UpdateAssetModel(
    storedAsset.value.organizationId, storedAsset.value.id, assetName.value,
    assetDescription.value, assetTeamsWebHook.value, storedAsset.value.eTag, selectedSiteId.value);
  try {
    const updated = await AssetService.updateAssetRecord(record);
    assetStore.setAsset(updated);
    storedAsset.value = assetStore.asset;
    resetFieldsFromStore();
  } finally {
    isSaving.value = false;
  }
}

function discardChanges() {
  resetFieldsFromStore();
}

// ── Teams test message (user-initiated). no-cors: the POST is delivered but the response is opaque,
// so we can only confirm it was dispatched, not the channel's acceptance. ──
async function sendTeamsTest() {
  if (!canSendTest.value) return;
  sendingTest.value = true;
  testResult.value = null;
  try {
    await fetch(assetTeamsWebHook.value.trim(), {
      method: 'POST',
      mode: 'no-cors',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: `✅ Test message from Duallo for “${storedAsset.value.name}”. Notifications are configured correctly.` }),
    });
    testResult.value = { ok: true, msg: 'Test message dispatched. The browser can’t confirm Teams accepted it — check your channel, and if nothing arrives, re-check the URL.' };
  } catch {
    testResult.value = { ok: false, msg: 'Could not reach that webhook URL.' };
  } finally {
    sendingTest.value = false;
  }
}

// ── Cover image upload ──
function pickFile() {
  fileInput.value?.click();
}

function onFilePicked(e) {
  const file = e.target?.files?.[0];
  if (file) uploadFile(file);
  if (fileInput.value) fileInput.value.value = '';
}

function onDrop(e) {
  isDragover.value = false;
  const file = e.dataTransfer?.files?.[0];
  if (file && file.type.startsWith('image/')) uploadFile(file);
}

async function uploadFile(file) {
  isUploading.value = true;
  coverPreview.value = URL.createObjectURL(file);
  try {
    const { base64String, fileExtension } = await getBase64AndExtension(file);
    await commitCover(base64String, fileExtension);
  } finally {
    finishUpload();
  }
}

async function useFrame(frame) {
  framesDialog.value = false;
  if (!frame?.url) return;
  isUploading.value = true;
  coverPreview.value = frame.url;
  try {
    const resp = await fetch(frame.url);
    const blob = await resp.blob();
    const base64String = await blobToBase64(blob);
    const fileExtension = (blob.type && blob.type.split('/')[1]) || 'jpg';
    await commitCover(base64String, fileExtension);
  } catch {
    /* leave existing image on failure */
  } finally {
    finishUpload();
  }
}

async function commitCover(base64String, fileExtension) {
  const request = new UpdateAssetImageModel(
    storedAsset.value.organizationId, storedAsset.value.id, base64String, fileExtension);
  const updated = await AssetService.updateAssetProfileImage(request);
  assetStore.setAsset(updated);
  storedAsset.value = assetStore.asset;
  imageSrc.value = await organizationSasService.getSignedUrl(storedAsset.value.imagePath);
  imageKey.value++;
}

function finishUpload() {
  isUploading.value = false;
  if (coverPreview.value && coverPreview.value.startsWith('blob:')) URL.revokeObjectURL(coverPreview.value);
  coverPreview.value = null;
}

function onCoverError() {
  imageSrc.value = null;
}

async function openFrames() {
  framesDialog.value = true;
  if (frameThumbs.value.length || framesLoading.value) return;
  framesLoading.value = true;
  try {
    if (!framesRaw.length) await probeFrames();
    const list = (framesRaw || []).filter(f => f.imagePath).slice(0, 60);
    frameThumbs.value = await Promise.all(list.map(async (f, i) => ({
      id: f.id || i,
      url: await organizationSasService.getSignedUrl(f.imagePath).catch(() => null),
    })));
  } finally {
    framesLoading.value = false;
  }
}

function blobToBase64(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(String(reader.result).split(',')[1]);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

// ── Archive (soft-delete) — same call as the asset-details Delete button (isActive → false),
// reversible by reactivating the asset from the admin area. ──
async function confirmDelete() {
  isDeleting.value = true;
  try {
    await AssetService.deleteAssetAsync(storedAsset.value.organizationId, storedAsset.value.id, false);
    assetStore.setAsset(null);
    assetStore.clearAsset();
    router.push({ name: 'Assets' });
  } catch {
    isDeleting.value = false;
  }
}

onUnmounted(() => {
  if (coverPreview.value && coverPreview.value.startsWith('blob:')) URL.revokeObjectURL(coverPreview.value);
});
</script>

<style scoped>
/* Reflow on the panel's OWN width (container query), so it collapses correctly regardless of the
   sidebar width or browser/OS zoom (125% / 150%) rather than guessing from the viewport. */
.gen-settings { container-type: inline-size; container-name: gen; min-height: 100%; }

.gen-grid {
  display: grid;
  grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
  gap: clamp(14px, calc(6px + 0.6vw), 18px);
  align-items: start;
  user-select: none;
}
.gen-col { display: flex; flex-direction: column; gap: clamp(14px, calc(6px + 0.6vw), 18px); min-width: 0; }

/* Cards */
.set-card {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 14px;
  box-shadow: 0 4px 14px rgba(0, 0, 0, 0.25);
  padding: clamp(16px, calc(8px + 0.6vw), 22px);
  min-width: 0;
}
.set-head { margin-bottom: 16px; }
.set-title { font-size: 1.02rem; font-weight: 700; color: #fff; margin: 0; }
.set-sub { margin: 4px 0 0; font-size: 0.85rem; color: #8aa0aa; line-height: 1.45; }
.set-body { display: flex; flex-direction: column; }

/* Fields */
.fld-label { font-size: 0.8rem; font-weight: 600; color: #b7c5cc; margin: 0 0 6px; }
.fld-label .fld-opt { color: #7f939d; font-weight: 500; }
.fld { margin-bottom: 16px; }
.fld:last-child { margin-bottom: 0; }

.webhook-row { display: flex; gap: 10px; align-items: flex-start; flex-wrap: wrap; }
.webhook-input { flex: 1 1 220px; min-width: 0; }
.webhook-result { margin: 8px 0 0; font-size: 0.82rem; }
.webhook-result.ok { color: #6ee7a0; }
.webhook-result.bad { color: #ff9c9c; }

/* Buttons */
.btn-outline {
  flex: 0 0 auto;
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 10px 16px; border-radius: 10px;
  background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.14);
  color: #e6edf0; font: inherit; font-size: 0.86rem; font-weight: 600; cursor: pointer;
  white-space: nowrap; min-height: 42px;
  transition: background 0.15s ease, border-color 0.15s ease, color 0.15s ease;
}
.btn-outline:hover:not(:disabled) { background: rgba(255, 255, 255, 0.08); }
.btn-outline.accent { color: var(--duallo-accent); border-color: rgba(100, 255, 218, 0.4); }
.btn-outline.accent:hover:not(:disabled) { background: rgba(100, 255, 218, 0.08); }
.btn-outline:disabled { opacity: 0.45; cursor: not-allowed; }

.btn-primary {
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 10px 20px; border-radius: 10px; min-width: 130px;
  background: var(--duallo-accent-gradient); color: #071514;
  border: none; font: inherit; font-weight: 700; cursor: pointer;
}
.btn-primary:disabled { opacity: 0.6; cursor: not-allowed; }

.btn-ghost {
  padding: 10px 18px; border-radius: 10px;
  background: rgba(255, 255, 255, 0.04); border: 1px solid rgba(255, 255, 255, 0.12);
  color: #cfd8dc; font: inherit; font-weight: 600; cursor: pointer;
}
.btn-ghost:disabled { opacity: 0.5; cursor: not-allowed; }

.btn-danger {
  flex: 0 0 auto;
  display: inline-flex; align-items: center; justify-content: center; gap: 6px;
  padding: 10px 18px; border-radius: 10px; min-width: 100px;
  background: rgba(239, 68, 68, 0.12); border: 1px solid rgba(239, 68, 68, 0.45);
  color: #ff9c9c; font: inherit; font-weight: 700; cursor: pointer;
  transition: background 0.15s ease;
}
.btn-danger:hover:not(:disabled) { background: rgba(239, 68, 68, 0.2); }
.btn-danger:disabled { opacity: 0.5; cursor: not-allowed; }

/* Cover image */
.cover-drop {
  position: relative;
  width: 100%;
  aspect-ratio: 16 / 9;
  border-radius: 12px;
  border: 1.5px dashed rgba(255, 255, 255, 0.16);
  background: rgba(0, 0, 0, 0.22);
  overflow: hidden;
  display: flex; align-items: center; justify-content: center;
}
.cover-drop.dragover { border-color: rgba(100, 255, 218, 0.6); background: rgba(100, 255, 218, 0.06); }
.cover-img { position: absolute; inset: 0; width: 100%; height: 100%; object-fit: cover; }
.cover-overlay {
  position: relative; z-index: 1;
  display: flex; flex-direction: column; align-items: center; justify-content: center; gap: 12px;
  text-align: center; padding: 16px;
}
/* When an image is present, sit the buttons on a scrim so they stay legible. */
.cover-overlay.has-image {
  position: absolute; inset: 0; justify-content: flex-end; padding-bottom: 16px;
  background: linear-gradient(180deg, rgba(10,15,18,0) 40%, rgba(10,15,18,0.6) 100%);
  opacity: 0; transition: opacity 0.15s ease;
}
.cover-drop:hover .cover-overlay.has-image { opacity: 1; }
.cover-text { font-size: 0.9rem; color: #9db0ba; }
.cover-actions { display: flex; gap: 10px; flex-wrap: wrap; justify-content: center; }

/* Danger card */
.danger-card { border-color: rgba(239, 68, 68, 0.28); padding: 18px 20px; }
.danger-row { display: flex; align-items: center; justify-content: space-between; gap: 16px; flex-wrap: wrap; }
.danger-text { min-width: 0; }

/* Save bar */
.save-bar {
  grid-column: 1 / -1;
  position: sticky; bottom: 0;
  display: flex; align-items: center; justify-content: space-between; gap: 14px; flex-wrap: wrap;
  padding: 12px 16px; border-radius: 12px;
  background: rgba(20, 28, 33, 0.96);
  border: 1px solid rgba(240, 181, 74, 0.35);
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.45);
}
.save-msg { display: inline-flex; align-items: center; gap: 4px; font-size: 0.88rem; font-weight: 600; color: #e6edf0; }
.save-actions { display: flex; gap: 10px; }
.savebar-enter-active, .savebar-leave-active { transition: opacity 0.15s ease, transform 0.15s ease; }
.savebar-enter-from, .savebar-leave-to { opacity: 0; transform: translateY(8px); }

/* Modals (frame picker + delete) */
.modal {
  background: var(--duallo-card-gradient);
  border: 1px solid rgba(255, 255, 255, 0.06);
  border-radius: 16px;
  padding: 22px;
  color: #fff;
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.45);
}
.modal-head { display: flex; align-items: center; justify-content: space-between; margin-bottom: 16px; }
.modal-title { font-size: 1.15rem; font-weight: 700; margin: 0; }
.modal-title.center { text-align: center; margin: 4px 0 10px; }
.modal-close {
  width: 34px; height: 34px; border-radius: 9px; background: transparent;
  border: 1px solid rgba(255, 255, 255, 0.08); color: #b0bec5; cursor: pointer;
  display: inline-flex; align-items: center; justify-content: center;
}
.modal-close:hover { background: rgba(255, 255, 255, 0.06); color: #fff; }
.modal-text { font-size: 0.9rem; color: #b0bec5; line-height: 1.5; margin: 0 0 16px; text-align: center; }
.modal-text strong { color: #fff; }
.modal-actions { display: flex; gap: 10px; justify-content: center; margin-top: 18px; }
.modal-actions .btn-ghost, .modal-actions .btn-danger { flex: 1 1 0; max-width: 170px; }

.danger-icon {
  width: 52px; height: 52px; margin: 0 auto 6px; border-radius: 14px;
  background: rgba(239, 68, 68, 0.1); border: 1px solid rgba(239, 68, 68, 0.25);
  display: flex; align-items: center; justify-content: center;
}

.frames-loading, .frames-empty { min-height: 160px; display: flex; align-items: center; justify-content: center; color: #8aa0aa; }
.frames-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(120px, 1fr)); gap: 10px;
  max-height: 60vh; overflow-y: auto; padding: 2px;
}
.frame-cell {
  aspect-ratio: 16 / 9; border-radius: 8px; overflow: hidden; cursor: pointer; padding: 0;
  border: 1px solid rgba(255, 255, 255, 0.08); background: #1c272c;
  display: flex; align-items: center; justify-content: center;
  transition: border-color 0.15s ease, transform 0.1s ease;
}
.frame-cell:hover { border-color: rgba(100, 255, 218, 0.6); transform: translateY(-2px); }
.frame-cell img { width: 100%; height: 100%; object-fit: cover; display: block; }

/* Stack when the panel itself gets narrow (covers 125% / 150% zoom + a wide sidebar). */
@container gen (max-width: 820px) {
  .gen-grid { grid-template-columns: 1fr; }
}
/* Fallback for engines without container queries: stack on a narrow viewport. */
@supports not (container-type: inline-size) {
  @media (max-width: 1100px) { .gen-grid { grid-template-columns: 1fr; } }
}
</style>
